import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Masonry } from 'masonic';
import { observer } from 'mobx-react';
import { Node } from 'types';

import { useMyFavoriteChannels, useMyFavoriteChannelsSettings } from '@kaltura-ott/tvpil-shared';

import Channel from 'components/pages/Settings/Favorites/Channel/Channel';
import { preloadChannelImage } from 'components/pages/Settings/Favorites/helper/preloadChannelImage';
import SettingsContentWrapper from 'components/pages/Settings/SettingsContentWrapper/SettingsContentWrapper';
import RailSkeleton from 'components/widgets/Rail/components/RailSkeleton/RailSkeleton';
import { railObserver } from 'components/widgets/Rail/utils';
import { useLoadingPerformanceEvents, useOnClickOutside } from 'hooks';
import { APPLICATION_SPINNER_EVENT_NAME } from 'utils';

import { useFavoriteHandlers } from './hooks/useFavoriteHandlers';
import {
  COLUMN,
  COLUMN_GUTTER,
  DEFAULT_HEIGHT,
  DEFAULT_HEIGHT_PLUS_MARGIN,
  DEFAULT_ITEMS_LENGTH,
  DEFAULT_MIN_WIDTH,
  DEFAULT_WIDTH,
  FAVORITES,
  FOUR_COLUMNS_RESOLUTION,
  HEADER_HEIGHT,
  MAX_GRID_WIDTH_FOR_DEFAULT_CARD_SIZE,
  MENU_HEIGHT,
  ROW_GUTTER,
  SETTINGS_TOP_PADDING,
} from './constants';

import styles from './Favorites.module.scss';

type FavoritesProps = { referenceId: string };

function Favorites({ referenceId }: FavoritesProps) {
  const intl = useIntl();
  const ref = useRef<HTMLDivElement | null>(null);
  const [isImagesLoading, setImagesLoading] = useState(false);
  const { channels, isLoading, refetch } = useMyFavoriteChannels({ referenceId });
  const { deleteFavorite, addFavorite } = useFavoriteHandlers();
  const settings = useMyFavoriteChannelsSettings({ referenceId });
  const channelList = channels || [];
  const isSkeletonVisible = isImagesLoading || isLoading;
  const countVisibleChannel =
    (Math.round(
      (window.innerHeight - MENU_HEIGHT - HEADER_HEIGHT - SETTINGS_TOP_PADDING) / DEFAULT_HEIGHT_PLUS_MARGIN,
    ) +
      1) *
    COLUMN;

  useEffect(() => {
    if (channelList.length) {
      const length = channelList.length > DEFAULT_ITEMS_LENGTH ? DEFAULT_ITEMS_LENGTH : channelList.length;
      preloadChannelImage(channelList.slice(0, length), setImagesLoading);
    }
  }, [channelList.length]);

  useLoadingPerformanceEvents(APPLICATION_SPINNER_EVENT_NAME.FAVORITES, isSkeletonVisible);

  const handleSkeletonRef = useCallback((node: Node) => {
    if (node) {
      railObserver.observe(node);
    }
  }, []);

  // it needs for updating data on 'favorite' click in setting bar
  useOnClickOutside(ref, (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;

    if (target.dataset.type === FAVORITES || target.closest('a')?.dataset.type === FAVORITES) {
      refetch();
    }
  });

  return (
    <SettingsContentWrapper
      title={intl.formatMessage({
        id: 'Settings.Favorites.SettingsContentWrapper.title',
        defaultMessage: 'Favorite Channels',
      })}
      description={intl.formatMessage({
        id: 'Settings.Favorites.SettingsContentWrapper.description',
        defaultMessage: 'Add or remove channels to create your personal list of channels',
      })}
      className={styles.favoriteWrapper}
      forwardRef={ref}
    >
      {isSkeletonVisible ? (
        <RailSkeleton
          className={styles.favorites}
          handleRef={handleSkeletonRef}
          isGrid
          numberOfCells={countVisibleChannel}
          settings={
            ({
              imageWidth: window.innerWidth > FOUR_COLUMNS_RESOLUTION ? DEFAULT_MIN_WIDTH : DEFAULT_WIDTH,
              imageHeight: DEFAULT_HEIGHT,
            })
          }
        />
      ) : (
        <Masonry
          style={{
            maxWidth: window.innerWidth < FOUR_COLUMNS_RESOLUTION ? MAX_GRID_WIDTH_FOR_DEFAULT_CARD_SIZE : '100%',
          }}
          className={styles.grid}
          items={channelList}
          itemKey={(data) => data.id}
          columnGutter={COLUMN_GUTTER}
          rowGutter={ROW_GUTTER}
          overscanBy={5}
          render={({ data: channel }) => (
            <Channel
              id={channel.id}
              images={channel.images}
              title={channel.title}
              settings={settings}
              isFavorite={channel.isFavorite}
              addFavorite={addFavorite}
              deleteFavorite={deleteFavorite}
            />
          )}
        />
      )}
    </SettingsContentWrapper>
  );
}

export default observer(Favorites);
